import React, { useCallback, useMemo } from 'react';

import { isUndefined } from 'lodash-es';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { useLoyaltyFeaturedRewardsQuery } from 'generated/sanity-graphql';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import useMediaQuery from 'hooks/use-media-query';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { routes } from 'utils/routing';

import {
  AvailablePointsCaption,
  BannerContainer,
  CurrentProgress,
  DashboardButton,
  PointsContainer,
  PointsProgress,
  PointsProgressContainer,
  RewardContainer,
  StyledLoyaltyPointsIcon,
  TierDot,
  UserRewardPointsContainer,
} from './loyalty-tiers-widget.styled';
import { NullableRewardTier, RewardTier } from './types';
import { getProgressBarValue, showTiersDynamically } from './utils';

const LoyaltyTiersWidget = () => {
  const { formatMessage } = useIntl();
  const { loyaltyUser } = useLoyaltyUser();
  const isMobile = useMediaQuery('mobile');
  const navigate = useNavigate();
  const { data, loading: featuredRewardsLoading } = useLoyaltyFeaturedRewardsQuery();
  const [featuredRewards] = data?.allRewardsSections || [];
  const { engineRewardsMap, loading: rewardsListLoading } = useLoyaltyRewardsList();
  const isLoading = featuredRewardsLoading || rewardsListLoading;
  const userPoints = loyaltyUser?.points ?? 0;
  const MAX_TIERS_POSITION = 7;
  const rewardTiersData = useMemo(() => {
    if (isLoading) {
      return [];
    }
    return featuredRewards?.rewardsList?.reduce((acc: RewardTier[], featuredReward) => {
      const { reward, category } = featuredReward || {};
      const engineReward = engineRewardsMap[reward?.loyaltyEngineId || ''];
      const { label: categoryLabel } = category || {};
      const { pointCost } = engineReward || {};

      const rewardTier = {
        topLabel: categoryLabel?.locale || '',
        pointsLabel: pointCost,
        id: reward?._id,
      };

      const allDataAvailable = (
        nullableRewardTier: NullableRewardTier
      ): nullableRewardTier is RewardTier => !Object.values(nullableRewardTier).some(isUndefined);

      if (allDataAvailable(rewardTier)) {
        acc.push(rewardTier);
      }
      return acc;
    }, []);
  }, [engineRewardsMap, featuredRewards, isLoading]);

  const handleNavigation = useCallback(() => {
    navigate(routes.rewardsHistory);
  }, [navigate]);

  const renderTiersDot = (initialValue: string, rewardTiersArray: RewardTier[]) => (
    <PointsProgressContainer>
      <PointsProgress>
        <TierDot completed={userPoints > 0} value={initialValue} />
        {rewardTiersArray.map((tier: RewardTier, index) => {
          const left = (100 / rewardTiersArray.length) * (index + 1);
          const completed = tier.pointsLabel <= userPoints;
          const rewardKey = tier.id || index;
          return (
            <>
              <TierDot
                data-testid={`tier-dot`}
                left={left}
                completed={completed}
                value={'' + tier.pointsLabel}
                key={rewardKey}
              />
            </>
          );
        })}
        <CurrentProgress $progress={getProgressBarValue(userPoints, rewardTiersArray)} />
      </PointsProgress>
    </PointsProgressContainer>
  );

  if (!rewardTiersData?.length) {
    return null;
  }

  const displayedRewardsData = showTiersDynamically(userPoints, rewardTiersData);

  const checkTiersSizeOnMobile = displayedRewardsData.length > MAX_TIERS_POSITION && isMobile;

  const displayedSplittedRewardsData = checkTiersSizeOnMobile
    ? displayedRewardsData.slice(MAX_TIERS_POSITION, displayedRewardsData.length)
    : null;

  const displayedOriginalSizeTiers = checkTiersSizeOnMobile
    ? displayedRewardsData.slice(0, MAX_TIERS_POSITION)
    : displayedRewardsData;

  return (
    <BannerContainer data-testid="loyalty-tiers-widget">
      <RewardContainer>
        <PointsContainer>
          <UserRewardPointsContainer>
            <StyledLoyaltyPointsIcon width="50" />
            <span data-testid="user-points">{userPoints}</span>
          </UserRewardPointsContainer>
          <AvailablePointsCaption>
            {formatMessage({ id: 'availablePoints' })}
          </AvailablePointsCaption>
        </PointsContainer>
        <DashboardButton onClick={handleNavigation}>
          {formatMessage({ id: 'rewardsHistory' })}
        </DashboardButton>
      </RewardContainer>
      {!!displayedSplittedRewardsData
        ? renderTiersDot(
            '' + displayedRewardsData[MAX_TIERS_POSITION - 1]?.pointsLabel,
            displayedSplittedRewardsData
          )
        : renderTiersDot('0', displayedOriginalSizeTiers)}
    </BannerContainer>
  );
};

export default LoyaltyTiersWidget;
